import html2canvas from 'html2canvas';

// --- Console Capture ---

interface ConsoleEntry {
  level: 'error' | 'warn';
  message: string;
  timestamp: string;
  stack?: string;
}

const CONSOLE_BUFFER_SIZE = 50;
const consoleBuffer: ConsoleEntry[] = [];
let consoleInterceptInitialized = false;

export function initConsoleCapture() {
  if (consoleInterceptInitialized) return;
  consoleInterceptInitialized = true;

  const originalError = console.error;
  const originalWarn = console.warn;

  console.error = (...args: unknown[]) => {
    addConsoleEntry('error', args);
    originalError.apply(console, args);
  };

  console.warn = (...args: unknown[]) => {
    addConsoleEntry('warn', args);
    originalWarn.apply(console, args);
  };
}

function addConsoleEntry(level: 'error' | 'warn', args: unknown[]) {
  const message = args
    .map((a) => {
      if (a instanceof Error) return `${a.message}\n${a.stack ?? ''}`;
      if (typeof a === 'object') {
        try {
          return JSON.stringify(a);
        } catch {
          return String(a);
        }
      }
      return String(a);
    })
    .join(' ');

  const entry: ConsoleEntry = {
    level,
    message,
    timestamp: new Date().toISOString(),
    stack: (args.find((a) => a instanceof Error) as Error | undefined)?.stack ?? undefined,
  };

  consoleBuffer.push(entry);
  if (consoleBuffer.length > CONSOLE_BUFFER_SIZE) {
    consoleBuffer.shift();
  }
}

export function getConsoleSnapshot(): ConsoleEntry[] {
  return [...consoleBuffer];
}

// --- Network Capture ---

interface NetworkEntry {
  url: string;
  method: string;
  status: number | null;
  duration: number | null;
  requestHeaders?: Record<string, string>;
  requestBody?: string | null;
  responseBody?: string | null;
  responseSize?: number | null;
  timestamp: string;
}

const NETWORK_BUFFER_SIZE = 30;
const networkBuffer: NetworkEntry[] = [];
let networkInterceptInitialized = false;

const SENSITIVE_HEADERS = ['authorization', 'cookie', 'set-cookie'];

function sanitizeHeaders(headers?: Record<string, string>): Record<string, string> {
  if (!headers) return {};
  const sanitized: Record<string, string> = {};
  for (const [key, value] of Object.entries(headers)) {
    if (SENSITIVE_HEADERS.includes(key.toLowerCase())) {
      sanitized[key] = '[REDACTED]';
    } else {
      sanitized[key] = value;
    }
  }
  return sanitized;
}

const BODY_MAX_LENGTH = 2000;

function truncateBody(body: string | null | undefined): string | null {
  if (!body) return null;
  if (body.length > BODY_MAX_LENGTH) {
    return body.substring(0, BODY_MAX_LENGTH) + `... [truncated - ${body.length} chars]`;
  }
  return body;
}

async function safeReadRequestBody(request: Request): Promise<string | null> {
  try {
    const cloned = request.clone();
    const text = await cloned.text();
    return truncateBody(text) || null;
  } catch {
    return null;
  }
}

async function safeReadResponseBody(response: Response): Promise<string | null> {
  try {
    const contentType = response.headers.get('content-type') ?? '';
    if (
      contentType.includes('image/') ||
      contentType.includes('octet-stream') ||
      contentType.includes('font/') ||
      contentType.includes('audio/') ||
      contentType.includes('video/')
    ) {
      return `[binary: ${contentType}]`;
    }
    const cloned = response.clone();
    const text = await cloned.text();
    return truncateBody(text) || null;
  } catch {
    return null;
  }
}

export function initNetworkCapture() {
  if (networkInterceptInitialized) return;
  networkInterceptInitialized = true;

  const { fetch: originalFetch } = window;

  window.fetch = async (...args) => {
    const startTime = Date.now();
    const request = new Request(...args);
    const headerObj: Record<string, string> = {};
    request.headers.forEach((value, key) => {
      headerObj[key] = value;
    });

    const reqBody = await safeReadRequestBody(request);

    try {
      const response = await originalFetch(...args);
      const resBody = await safeReadResponseBody(response);
      const entry: NetworkEntry = {
        url: request.url,
        method: request.method,
        status: response.status,
        duration: Date.now() - startTime,
        requestHeaders: sanitizeHeaders(headerObj),
        requestBody: reqBody,
        responseBody: resBody,
        responseSize: null,
        timestamp: new Date().toISOString(),
      };
      addNetworkEntry(entry);
      return response;
    } catch (error) {
      const entry: NetworkEntry = {
        url: request.url,
        method: request.method,
        status: null,
        duration: Date.now() - startTime,
        requestHeaders: sanitizeHeaders(headerObj),
        requestBody: reqBody,
        responseBody: null,
        responseSize: null,
        timestamp: new Date().toISOString(),
      };
      addNetworkEntry(entry);
      throw error;
    }
  };

  // Also intercept XMLHttpRequest for Axios
  const originalOpen = XMLHttpRequest.prototype.open;
  const originalSend = XMLHttpRequest.prototype.send;
  const originalSetRequestHeader = XMLHttpRequest.prototype.setRequestHeader;

  XMLHttpRequest.prototype.open = function (method: string, url: string | URL, ...rest: unknown[]) {
    (
      this as XMLHttpRequest & {
        _feedbackMeta: {
          method: string;
          url: string;
          startTime: number;
          headers: Record<string, string>;
          requestBody: string | null;
        };
      }
    )._feedbackMeta = { method, url: String(url), startTime: 0, headers: {}, requestBody: null };
    return originalOpen.apply(this, [method, url, ...rest] as Parameters<typeof originalOpen>);
  };

  XMLHttpRequest.prototype.setRequestHeader = function (name: string, value: string) {
    const meta = (this as XMLHttpRequest & { _feedbackMeta?: { headers: Record<string, string> } })._feedbackMeta;
    if (meta) {
      meta.headers[name] = value;
    }
    return originalSetRequestHeader.apply(this, [name, value]);
  };

  XMLHttpRequest.prototype.send = function (...args) {
    const meta = (
      this as XMLHttpRequest & {
        _feedbackMeta?: {
          method: string;
          url: string;
          startTime: number;
          headers: Record<string, string>;
          requestBody: string | null;
        };
      }
    )._feedbackMeta;
    if (meta) {
      meta.startTime = Date.now();
      if (args[0] != null) {
        meta.requestBody = truncateBody(typeof args[0] === 'string' ? args[0] : JSON.stringify(args[0]));
      }
      this.addEventListener('loadend', () => {
        let resBody: string | null = null;
        try {
          if (this.responseType === '' || this.responseType === 'text') {
            resBody = truncateBody(this.responseText);
          } else if (this.responseType === 'json' && this.response) {
            resBody = truncateBody(JSON.stringify(this.response));
          }
        } catch {
          /* ignore */
        }
        const entry: NetworkEntry = {
          url: meta.url,
          method: meta.method,
          status: this.status || null,
          duration: Date.now() - meta.startTime,
          requestHeaders: sanitizeHeaders(meta.headers),
          requestBody: meta.requestBody,
          responseBody: resBody,
          responseSize: this.response ? (typeof this.response === 'string' ? this.response.length : null) : null,
          timestamp: new Date().toISOString(),
        };
        addNetworkEntry(entry);
      });
    }
    return originalSend.apply(this, args as Parameters<typeof originalSend>);
  };
}

function addNetworkEntry(entry: NetworkEntry) {
  networkBuffer.push(entry);
  if (networkBuffer.length > NETWORK_BUFFER_SIZE) {
    networkBuffer.shift();
  }
}

export function getNetworkSnapshot(): NetworkEntry[] {
  return [...networkBuffer];
}

// --- Screenshot Capture ---

/**
 * Convert a single oklch() colour token to rgb() using the Canvas 2D API.
 * The canvas natively supports oklch, so we paint a pixel and read it back.
 */
const _oklchCache = new Map<string, string>();
function oklchTokenToRgb(token: string): string {
  const cached = _oklchCache.get(token);
  if (cached) return cached;
  try {
    const cvs = document.createElement('canvas');
    cvs.width = 1;
    cvs.height = 1;
    const ctx = cvs.getContext('2d');
    if (!ctx) return token;
    ctx.fillStyle = 'transparent';
    ctx.fillStyle = token;
    ctx.fillRect(0, 0, 1, 1);
    const [r, g, b, a] = ctx.getImageData(0, 0, 1, 1).data;
    const rgb = a < 255
      ? `rgba(${r},${g},${b},${(a / 255).toFixed(3)})`
      : `rgb(${r},${g},${b})`;
    _oklchCache.set(token, rgb);
    return rgb;
  } catch {
    return token;
  }
}

/** Replace every oklch(...) occurrence in a CSS value string with its rgb() equivalent. */
const OKLCH_RE = /oklch\([^)]*\)/gi;
function replaceOklchInValue(value: string): string {
  return value.replace(OKLCH_RE, (match) => oklchTokenToRgb(match));
}

/** Recursively patch all CSS rules in a rule list, converting oklch → rgb. */
function patchCssRuleList(rules: CSSRuleList) {
  for (let i = 0; i < rules.length; i++) {
    const rule = rules[i];
    // Patch any rule that exposes a CSSStyleDeclaration (style rules, font-face, keyframe, etc.)
    if ('style' in rule) {
      const style = (rule as { style: CSSStyleDeclaration }).style;
      for (let j = 0; j < style.length; j++) {
        const prop = style[j];
        const val = style.getPropertyValue(prop);
        if (val.includes('oklch')) {
          style.setProperty(prop, replaceOklchInValue(val));
        }
      }
    }
    // Recurse into grouping rules (@media, @supports, @layer, etc.)
    if ('cssRules' in rule) {
      patchCssRuleList((rule as CSSGroupingRule).cssRules);
    }
  }
}

/**
 * Patch a cloned document so html2canvas can render it.
 * html2canvas v1.x does not support the oklch() colour function and throws
 * during its CSS parsing phase. We replace ALL oklch occurrences with their
 * rgb() equivalents at three levels:
 *   1) <style> tag text content  – forces the browser to re-parse the
 *      stylesheet from scratch so getComputedStyle returns rgb values.
 *   2) Inline style attributes   – covers any element-level oklch usage.
 *   3) CSSOM rules for <link>    – handles external stylesheets that are
 *      same-origin (cross-origin ones are silently skipped).
 */
function patchOklchColors(clonedDoc: Document) {
  // 1. Replace oklch in all <style> tag text content
  //    This is the most reliable path: the browser will re-parse the
  //    stylesheet and getComputedStyle will automatically return rgb.
  clonedDoc.querySelectorAll('style').forEach((styleEl) => {
    const text = styleEl.textContent;
    if (text && OKLCH_RE.test(text)) {
      OKLCH_RE.lastIndex = 0;
      styleEl.textContent = text.replace(OKLCH_RE, (m) => oklchTokenToRgb(m));
    }
  });

  // 2. Replace oklch in inline style attributes
  clonedDoc.querySelectorAll('[style]').forEach((el) => {
    const attr = el.getAttribute('style') ?? '';
    if (OKLCH_RE.test(attr)) {
      OKLCH_RE.lastIndex = 0;
      el.setAttribute('style', attr.replace(OKLCH_RE, (m) => oklchTokenToRgb(m)));
    }
  });

  // 3. Patch CSSOM rules for external <link> stylesheets (same-origin only)
  for (const sheet of Array.from(clonedDoc.styleSheets)) {
    if (sheet.ownerNode instanceof HTMLStyleElement) continue; // already handled
    try {
      patchCssRuleList(sheet.cssRules);
    } catch {
      // cross-origin – skip
    }
  }

  // 4. Force layout / style re-computation
  void clonedDoc.documentElement.offsetHeight;
}

export async function captureScreenshot(): Promise<string | null> {
  try {
    const canvas = await html2canvas(document.body, {
      useCORS: true,
      allowTaint: false,
      logging: false,
      width: window.innerWidth,
      height: window.innerHeight,
      windowWidth: window.innerWidth,
      windowHeight: window.innerHeight,
      x: window.scrollX,
      y: window.scrollY,
      scale: 1,
      foreignObjectRendering: false,
      ignoreElements: (el: Element) => {
        const tag = el.tagName?.toLowerCase();
        return tag === 'iframe' || tag === 'video' || tag === 'object' || tag === 'embed';
      },
      onclone: (clonedDoc: Document) => {
        patchOklchColors(clonedDoc);
      },
    });
    const dataUrl = canvas.toDataURL('image/png', 0.8);
    return dataUrl.replace(/^data:image\/png;base64,/, '');
  } catch (error) {
    console.warn('Failed to capture screenshot:', error);
    return null;
  }
}

// --- Device Info ---

export interface DeviceInfo {
  userAgent: string;
  browser: string;
  os: string;
  viewportWidth: number;
  viewportHeight: number;
  screenWidth: number;
  screenHeight: number;
  language: string;
  currentUrl: string;
  timestamp: string;
}

export function getDeviceInfo(): DeviceInfo {
  const ua = navigator.userAgent;

  let browser = 'Unknown';
  if (ua.includes('Firefox/')) browser = `Firefox ${ua.split('Firefox/')[1]?.split(' ')[0] ?? ''}`;
  else if (ua.includes('Edg/')) browser = `Edge ${ua.split('Edg/')[1]?.split(' ')[0] ?? ''}`;
  else if (ua.includes('Chrome/')) browser = `Chrome ${ua.split('Chrome/')[1]?.split(' ')[0] ?? ''}`;
  else if (ua.includes('Safari/')) browser = `Safari ${ua.split('Version/')[1]?.split(' ')[0] ?? ''}`;

  let os = 'Unknown';
  if (ua.includes('Windows NT 10')) os = 'Windows 10/11';
  else if (ua.includes('Windows NT')) os = 'Windows';
  else if (ua.includes('Mac OS X'))
    os = `macOS ${ua.split('Mac OS X ')[1]?.split(')')[0]?.replace(/_/g, '.') ?? ''}`;
  else if (ua.includes('Linux')) os = 'Linux';
  else if (ua.includes('Android')) os = `Android ${ua.split('Android ')[1]?.split(';')[0] ?? ''}`;
  else if (ua.includes('iPhone') || ua.includes('iPad')) os = 'iOS';

  return {
    userAgent: ua,
    browser: browser.trim(),
    os: os.trim(),
    viewportWidth: window.innerWidth,
    viewportHeight: window.innerHeight,
    screenWidth: window.screen.width,
    screenHeight: window.screen.height,
    language: navigator.language,
    currentUrl: window.location.href,
    timestamp: new Date().toISOString(),
  };
}
